import React, { useState } from "react";
import { X, AlertCircle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number | null;
  orderNumber?: string;
  orderType?: "medicine" | "labtest";
  onSuccess?: () => void;
}

const MEDICINE_REASONS = [
  "Medicine no longer needed",
  "Patient requested cancellation",
  "Product out of stock",
  "Incorrect order items",
  "Duplicate order",
];

const LABTEST_REASONS = [
  "Test no longer needed",
  "Patient requested cancellation",
  "Sample collection issue",
  "Lab slot unavailable",
  "Duplicate test order",
];

export const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  orderId,
  orderNumber,
  orderType = "medicine",
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen || orderId === null) return null;

  const presetReasons = orderType === "labtest" ? LABTEST_REASONS : MEDICINE_REASONS;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalReason = reason.trim();
    if (!finalReason) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      setLoading(true);
      const res = await AdminOrderManagementService.cancelOrder({
        orderId,
        cancelledBy: "patient",
        cancelationReason: finalReason,
      });
      if (res.success) {
        toast.success(res.message || "Order cancelled successfully!");
        setReason("");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to cancel order");
      }
    } catch (error) {
      console.error("Cancel order error:", error);
      toast.error("Failed to cancel order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[500000] flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-[2px] animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-5 flex flex-col gap-4 animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600">
              <AlertCircle size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">
                Cancel {orderType === "labtest" ? "Labtest Order" : "Order"}
              </h3>
              {orderNumber && (
                <p className="text-[0.7rem] text-slate-500 font-medium">
                  Order #{orderNumber}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
          >
            <X size={15} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-slate-700">
              Reason for Cancellation <span className="text-rose-500">*</span>
            </label>

            {/* Quick Reason Chips */}
            <div className="flex flex-wrap gap-1.5 mb-1">
              {presetReasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className={`text-[0.7rem] px-2.5 py-1 rounded-md border font-medium transition-colors cursor-pointer ${
                    reason === r
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Enter or select ${orderType === "labtest" ? "labtest " : ""}cancellation reason...`}
              className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-slate-400 focus:outline-none text-slate-800 placeholder:text-slate-400 resize-none"
              disabled={loading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim()}
              className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 border border-rose-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
            >
              {loading ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Cancelling...
                </>
              ) : (
                "Confirm Cancel"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelOrderModal;
